'use client';

import { useEffect, useState } from 'react';
import { useTerminalStore } from '../../store/terminalStore';

const OUTPUT_LIMIT = 12_000;

export type LiveTerminal = {
  sessionId: string;
  title: string;
  output: string;
  running: boolean;
};

const ANSI_ESCAPE = String.fromCharCode(0x1b);
const ANSI_SEQUENCE = new RegExp(`${ANSI_ESCAPE}\\[[0-9;?]*[A-Za-z]`, 'g');

const stripTerminalControls = (value: string): string =>
  value.replace(ANSI_SEQUENCE, '').replace(/\r/g, '');

const sessionBelongsToChat = ({
  sessionId,
  chatId,
}: {
  sessionId: string;
  chatId: string;
}): boolean => sessionId === chatId || sessionId.startsWith(`${chatId}:`);

export function useLiveChatTerminals(chatId: string): LiveTerminal[] {
  const terminalTabs = useTerminalStore((state) => state.terminalTabs);
  const runningCommandSessionIds = useTerminalStore(
    (state) => state.runningCommandSessionIds,
  );
  const [sessions, setSessions] = useState<Record<string, LiveTerminal>>({});

  useEffect(() => {
    const api = window.electronAPI?.terminal;

    if (!api) {
      return;
    }

    const matching = terminalTabs.filter((tab) =>
      sessionBelongsToChat({ sessionId: tab.sessionId, chatId }),
    );
    const unsubscribers = matching.flatMap((tab) => {
      const emptySession: LiveTerminal = {
        sessionId: tab.sessionId,
        title: tab.title || tab.sessionId,
        output: '',
        running: true,
      };

      setSessions((current) => ({
        ...current,
        [tab.sessionId]: current[tab.sessionId] ?? emptySession,
      }));

      return [
        api.onData(tab.sessionId, (data) => {
          const chunk = stripTerminalControls(data);

          setSessions((current) => {
            const existing = current[tab.sessionId] ?? emptySession;
            const output = `${existing.output}${chunk}`.slice(-OUTPUT_LIMIT);

            return {
              ...current,
              [tab.sessionId]: {
                ...existing,
                title: tab.title || existing.title,
                output,
                running: true,
              },
            };
          });
        }),
        api.onExit(tab.sessionId, () => {
          setSessions((current) => {
            const existing = current[tab.sessionId];

            if (!existing) {
              return current;
            }

            return {
              ...current,
              [tab.sessionId]: { ...existing, running: false },
            };
          });
        }),
      ];
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [chatId, terminalTabs]);

  const activeSessionIds = new Set(
    terminalTabs
      .filter((tab) =>
        sessionBelongsToChat({ sessionId: tab.sessionId, chatId }),
      )
      .map((tab) => tab.sessionId),
  );

  return Object.values(sessions).filter(
    (terminal) =>
      terminal.running &&
      activeSessionIds.has(terminal.sessionId) &&
      (runningCommandSessionIds[terminal.sessionId] ?? 0) > 0,
  );
}
